import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Car, User, Camera, Upload, Shield, Check, X,
  Loader2, Globe, MapPin, Mail, Lock, Phone, CheckCircle, Eye, EyeOff, FileText
} from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { api } from '../services/api';
import { Country, City } from 'country-state-city';
import { SearchableSelect } from '../components/SearchableSelect';
import { compressIfImage } from '../utils/imageCompress';

const VEHICLE_TYPES = [
  { id: 'economy', name: 'Economy', passengers: 4, luggage: 3 },
  { id: 'comfort', name: 'Comfort', passengers: 4, luggage: 3 },
  { id: 'business', name: 'Business', passengers: 3, luggage: 3 },
  { id: 'premium', name: 'Premium', passengers: 3, luggage: 3 },
  { id: 'vip', name: 'VIP', passengers: 3, luggage: 4 },
  { id: 'suv', name: 'SUV', passengers: 6, luggage: 5 },
  { id: 'van', name: 'Van', passengers: 8, luggage: 8 },
  { id: 'minibus', name: 'Minibus', passengers: 16, luggage: 16 },
  { id: 'bus', name: 'Bus', passengers: 50, luggage: 50 },
];

const LANGUAGES = [
  { id: 'ar', name: 'Arabisch' },
  { id: 'en', name: 'Englisch' },
  { id: 'de', name: 'Deutsch' },
  { id: 'ru', name: 'Russisch' },
  { id: 'fr', name: 'Französisch' },
  { id: 'it', name: 'Italienisch' },
];

const countryOptions = Country.getAllCountries()
  .sort((a, b) => a.name.localeCompare(b.name))
  .map(c => ({ value: c.isoCode, label: `${c.flag} ${c.name}` }));

const STEPS = [
  { num: 1, label: 'Konto' },
  { num: 2, label: 'Verifizierung' },
  { num: 3, label: 'Fahrer-Info' },
  { num: 4, label: 'Dokumente' },
];

// ─── Doc Upload Item ───
function DocItem({ docKey, label, required, isUploaded, isUploading, onCamera, onUpload }) {
  return (
    <div
      className={`p-4 rounded-xl border-2 transition-all ${
        isUploaded
          ? 'border-emerald-300 bg-emerald-50/60'
          : required
          ? 'border-amber-300 bg-amber-50/40'
          : 'border-slate-200 bg-slate-50'
      }`}
      data-testid={`doc-item-${docKey}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {isUploaded ? (
            <CheckCircle size={18} className="text-emerald-500" />
          ) : (
            <Shield size={18} className={required ? 'text-amber-500' : 'text-slate-400'} />
          )}
          <span className="font-semibold text-slate-800 text-sm">{label}</span>
        </div>
        {isUploaded ? (
          <span className="text-xs font-bold text-emerald-700 bg-emerald-100 px-2.5 py-1 rounded-full">Hochgeladen</span>
        ) : (
          <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${required ? 'text-amber-700 bg-amber-100' : 'text-slate-500 bg-slate-200'}`}>
            {required ? 'Pflicht' : 'Optional'}
          </span>
        )}
      </div>
      {isUploading ? (
        <div className="flex items-center justify-center py-3">
          <Loader2 size={22} className="animate-spin text-orange-500" />
          <span className="ml-2 text-sm text-slate-500">Wird hochgeladen...</span>
        </div>
      ) : (
        <div className="flex gap-2">
          <button
            onClick={onCamera}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 transition-colors"
            data-testid={`doc-camera-${docKey}`}
          >
            <Camera size={16} /> Kamera
          </button>
          <button
            onClick={onUpload}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-100 transition-colors"
            data-testid={`doc-upload-${docKey}`}
          >
            <Upload size={16} /> Hochladen
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───
export default function DriverRegister() {
  const navigate = useNavigate();
  const { user, token, loadFromStorage, register, verifyEmail, resendCode, login } = useAuthStore();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [existingDriver, setExistingDriver] = useState(null);
  const [checkingProfile, setCheckingProfile] = useState(true);

  // Step 1: Account creation
  const [accountData, setAccountData] = useState({ name: '', email: '', password: '', phone: '' });
  const [showPassword, setShowPassword] = useState(false);

  // Step 2: Verification
  const [verifyCode, setVerifyCode] = useState('');
  const [verifyEmail_, setVerifyEmail_] = useState('');
  const [resending, setResending] = useState(false);

  // Step 3: Driver info
  const [formData, setFormData] = useState({
    country: '', region: '', isCompany: false, companyName: '', languages: ['ar'],
    vehicle: { make: '', model: '', year: new Date().getFullYear(), color: '', plateNumber: '', vehicleType: 'economy', passengers: 4, luggage: 3 },
  });

  // Step 4: Document upload
  const [vehicleId, setVehicleId] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [docUploaded, setDocUploaded] = useState({});
  const [docStatus, setDocStatus] = useState(null);

  const cityOptions = useMemo(() => {
    if (!formData.country) return [];
    const cities = City.getCitiesOfCountry(formData.country);
    if (!cities || cities.length === 0) return [];
    const seen = new Set();
    return cities.filter(c => { if (seen.has(c.name)) return false; seen.add(c.name); return true; })
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(c => ({ value: c.name, label: `${c.name}${c.stateCode ? ` (${c.stateCode})` : ''}` }));
  }, [formData.country]);

  useEffect(() => { loadFromStorage(); }, [loadFromStorage]);

  useEffect(() => {
    if (token) {
      checkExistingDriver();
    } else {
      setCheckingProfile(false);
    }
  }, [token]);

  const checkExistingDriver = async () => {
    try {
      const res = await api.get('/driver/profile');
      if (res.data) {
        setExistingDriver(res.data);
      }
    } catch {}
    setCheckingProfile(false);
  };

  // If user is logged in, skip to step 3
  useEffect(() => {
    if (user && token && !existingDriver && !checkingProfile) {
      setStep(3);
    }
  }, [user, token, existingDriver, checkingProfile]);

  // ─── Step 1 Handlers ───
  const handleAccountCreate = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await register(accountData.email, accountData.password, accountData.name, accountData.phone);
      if (result.requires_verification) {
        setVerifyEmail_(result.email);
        setStep(2);
      } else if (result.user) {
        setStep(3);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      const isAlreadyRegistered = err.response?.status === 400
        && /bereits registriert|already registered|already exists/i.test(detail);

      if (isAlreadyRegistered) {
        // User has an existing customer account — try auto-login with the given password
        // so they can proceed directly to the driver registration steps.
        try {
          const loginResult = await login(accountData.email, accountData.password);
          if (loginResult?.requires_2fa) {
            setError('Mit dieser E-Mail existiert bereits ein Konto mit 2-Faktor-Authentifizierung. Bitte melden Sie sich zuerst über die Startseite an und klicken Sie dann auf "Fahrer werden".');
            return;
          }
          if (loginResult?.user) {
            // Logged in successfully — useEffect will jump to step 3.
            // For safety set it explicitly too:
            setStep(3);
            return;
          }
        } catch (loginErr) {
          const loginDetail = loginErr.response?.data?.detail || '';
          if (/email not verified|nicht verifiziert/i.test(loginDetail)) {
            // Re-trigger register to send a fresh verification code
            setVerifyEmail_(accountData.email);
            setStep(2);
            setError('Ihr Konto ist noch nicht verifiziert. Wir haben einen neuen Code an Ihre E-Mail gesendet.');
            // Trigger resend
            try { await resendCode(accountData.email); } catch {}
            return;
          }
          setError('Mit dieser E-Mail existiert bereits ein Konto. Das eingegebene Passwort stimmt nicht. Bitte melden Sie sich über die Startseite an und klicken Sie dann auf "Fahrer werden", oder verwenden Sie eine andere E-Mail-Adresse.');
          return;
        }
      }
      setError(detail || 'Registrierung fehlgeschlagen. Bitte versuchen Sie es erneut.');
    } finally {
      setLoading(false);
    }
  };

  // ─── Step 2 Handlers ───
  const handleVerifyEmail = async () => {
    setLoading(true);
    setError('');
    try {
      await verifyEmail(verifyEmail_, verifyCode);
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.detail || 'Falscher Code. Bitte erneut versuchen.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResending(true);
    try {
      await resendCode(verifyEmail_);
    } catch {}
    setTimeout(() => setResending(false), 3000);
  };

  // ─── Step 3 Handlers ───
  const handleInputChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({ ...prev, [parent]: { ...prev[parent], [child]: value } }));
    } else if (field === 'country') {
      setFormData(prev => ({ ...prev, country: value, region: '' }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleVehicleTypeChange = (typeId) => {
    const type = VEHICLE_TYPES.find(t => t.id === typeId);
    setFormData(prev => ({
      ...prev,
      vehicle: { ...prev.vehicle, vehicleType: typeId, passengers: type?.passengers || 4, luggage: type?.luggage || 3 },
    }));
  };

  const handleLanguageToggle = (langId) => {
    setFormData(prev => ({
      ...prev,
      languages: prev.languages.includes(langId) ? prev.languages.filter(l => l !== langId) : [...prev.languages, langId],
    }));
  };

  const handleDriverRegister = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.post('/driver/register', {
        country: formData.country, region: formData.region,
        vehicle: {
          make: formData.vehicle.make, model: formData.vehicle.model,
          year: parseInt(formData.vehicle.year), color: formData.vehicle.color,
          plate_number: formData.vehicle.plateNumber, vehicle_type: formData.vehicle.vehicleType,
          passengers: formData.vehicle.passengers, luggage: formData.vehicle.luggage,
        },
        is_company: formData.isCompany, company_name: formData.companyName || null, languages: formData.languages,
      });
      setVehicleId(res.data.vehicle_id);
      // Load fresh doc status
      try {
        const docRes = await api.get('/driver/documents-status');
        setDocStatus(docRes.data);
      } catch {}
      setStep(4);
    } catch (err) {
      setError(err.response?.data?.detail || 'Registrierung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  // ─── Step 4: Document upload handlers ───
  const triggerFileInput = (callback, useCamera = false) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,.pdf';
    if (useCamera) input.capture = 'environment';
    input.onchange = (e) => { if (e.target.files[0]) callback(e.target.files[0]); };
    input.click();
  };

  // Compress image to max 1920px, JPEG quality 0.82 → keeps document readable, ~300-700 KB
  // PDFs and other files pass through unchanged.
  // (Moved to /utils/imageCompress.js — also used by VehicleManager, DriverProfileTab, PWA)

  const handlePersonalDocUpload = async (docType, file) => {
    setUploadingDoc(docType);
    try {
      const dataUrl = await compressIfImage(file);
      await api.post(`/driver/upload-document/${docType}`, { document: dataUrl });
      setDocUploaded(prev => ({ ...prev, [docType]: true }));
      const res = await api.get('/driver/documents-status');
      setDocStatus(res.data);
    } catch (err) {
      console.error('Upload error:', err);
      const msg = err.response?.data?.detail || err.message || 'Upload fehlgeschlagen';
      alert(`Upload fehlgeschlagen: ${msg}`);
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleVehicleDocUpload = async (vId, docType, file) => {
    const key = `${vId}_${docType}`;
    setUploadingDoc(key);
    try {
      const dataUrl = await compressIfImage(file);
      await api.post(`/driver/vehicle/${vId}/upload-document/${docType}`, { document: dataUrl });
      setDocUploaded(prev => ({ ...prev, [key]: true }));
      const res = await api.get('/driver/documents-status');
      setDocStatus(res.data);
    } catch (err) {
      console.error('Upload error:', err);
      const msg = err.response?.data?.detail || err.message || 'Upload fehlgeschlagen';
      alert(`Upload fehlgeschlagen: ${msg}`);
    } finally {
      setUploadingDoc(null);
    }
  };

  const isPersonalDocUploaded = (key) => docUploaded[key] || docStatus?.personal_documents?.[key];

  const handleFinishDocs = () => {
    navigate('/driver/dashboard');
  };

  const handleSkipDocs = () => {
    navigate('/driver/dashboard');
  };

  // ─── Loading state ───
  if (checkingProfile) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-orange-500" />
      </div>
    );
  }

  // ─── Existing driver state ───
  if (existingDriver) {
    return (
      <div className="min-h-screen bg-slate-50">
        <NavBar />
        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 text-center">
            {existingDriver.status === 'pending' && (
              <>
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Loader2 size={32} className="text-yellow-600 animate-spin" />
                </div>
                <h2 className="text-xl font-bold text-slate-800 mb-2">Bewerbung in Prüfung</h2>
                <p className="text-slate-500 mb-4">Ihre Fahrerbewerbung wird derzeit geprüft. Sie können bereits Ihre Dokumente im Dashboard hochladen.</p>
                <a href="/driver/dashboard" className="inline-block bg-orange-500 text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-orange-600 transition-colors">
                  Zum Dashboard
                </a>
              </>
            )}
            {existingDriver.status === 'approved' && (
              <>
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check size={32} className="text-emerald-600" />
                </div>
                <h2 className="text-xl font-bold text-slate-800 mb-2">Sie sind ein aktiver Fahrer!</h2>
                <p className="text-slate-500 mb-4">Ihre Bewerbung wurde genehmigt.</p>
                <a href="/driver/dashboard" className="inline-block bg-orange-500 text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-orange-600 transition-colors">
                  Zum Dashboard
                </a>
              </>
            )}
            {existingDriver.status === 'rejected' && (
              <>
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <X size={32} className="text-red-600" />
                </div>
                <h2 className="text-xl font-bold text-slate-800 mb-2">Bewerbung abgelehnt</h2>
                <p className="text-slate-500">Leider wurde Ihre Bewerbung abgelehnt. Bitte kontaktieren Sie uns für weitere Informationen.</p>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ─── Step progress indicator ───
  const activeSteps = (!user || !token) ? STEPS : STEPS.filter(s => s.num >= 3);
  const progressStep = (!user || !token) ? step : step;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="driver-register-page">
      <NavBar />

      <div className="max-w-2xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-2" data-testid="driver-register-title">Werden Sie Fahrer</h1>
        <p className="text-slate-500 mb-8">Registrieren Sie sich, um Transfer-Anfragen zu erhalten und Geld zu verdienen.</p>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8 gap-2">
          {activeSteps.map((s, idx) => (
            <React.Fragment key={s.num}>
              <div className="flex flex-col items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-colors ${
                  progressStep > s.num ? 'bg-emerald-500 text-white' : progressStep === s.num ? 'bg-orange-500 text-white' : 'bg-slate-200 text-slate-500'
                }`}>
                  {progressStep > s.num ? <Check size={18} /> : s.num}
                </div>
                <span className="text-xs text-slate-500 mt-1 whitespace-nowrap">{s.label}</span>
              </div>
              {idx < activeSteps.length - 1 && (
                <div className={`w-12 sm:w-20 h-1 mt-[-12px] rounded-full ${progressStep > s.num ? 'bg-emerald-500' : 'bg-slate-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          {step === 1 && <Step1AccountCreate data={accountData} setData={setAccountData} showPassword={showPassword} setShowPassword={setShowPassword} loading={loading} error={error} onSubmit={handleAccountCreate} />}
          {step === 2 && <Step2Verify code={verifyCode} setCode={setVerifyCode} email={verifyEmail_} loading={loading} error={error} resending={resending} onVerify={handleVerifyEmail} onResend={handleResendCode} />}
          {step === 3 && <Step3DriverInfo formData={formData} cityOptions={cityOptions} onInputChange={handleInputChange} onVehicleTypeChange={handleVehicleTypeChange} onLanguageToggle={handleLanguageToggle} loading={loading} error={error} onSubmit={handleDriverRegister} />}
          {step === 4 && <Step4Documents vehicleId={vehicleId} docStatus={docStatus} uploadingDoc={uploadingDoc} docUploaded={docUploaded} isPersonalDocUploaded={isPersonalDocUploaded} onPersonalUpload={handlePersonalDocUpload} onVehicleUpload={handleVehicleDocUpload} triggerFileInput={triggerFileInput} onFinish={handleFinishDocs} onSkip={handleSkipDocs} vehicleName={`${formData.vehicle.make} ${formData.vehicle.model}`} />}
        </div>
      </div>
    </div>
  );
}

// ─── Navbar ───
function NavBar() {
  return (
    <nav className="bg-white border-b border-slate-100 px-6 py-4">
      <div className="max-w-4xl mx-auto flex items-center gap-4">
        <a href="/" className="flex items-center gap-2 text-slate-500 hover:text-slate-700 text-sm">
          <ArrowLeft size={18} /> Zurück
        </a>
        <div className="flex items-baseline gap-0.5">
          <span className="text-2xl font-extrabold text-orange-500">366</span>
          <span className="text-sm font-semibold text-slate-800">Transfer</span>
        </div>
      </div>
    </nav>
  );
}

// ─── Step 1: Account Creation ───
function Step1AccountCreate({ data, setData, showPassword, setShowPassword, loading, error, onSubmit }) {
  const update = (field, val) => setData(prev => ({ ...prev, [field]: val }));
  const [agbAccepted, setAgbAccepted] = useState(false);
  const canSubmit = data.name.trim() && data.email.trim() && data.password.length >= 6 && data.phone.trim() && agbAccepted;

  return (
    <div className="p-6 space-y-5" data-testid="step-account-create">
      <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
        <User size={20} className="text-orange-500" /> Konto erstellen
      </h2>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">Vollständiger Name *</label>
        <div className="relative">
          <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text" value={data.name} onChange={(e) => update('name', e.target.value)}
            className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/10 outline-none"
            placeholder="Max Mustermann" data-testid="reg-name" />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">E-Mail-Adresse *</label>
        <div className="relative">
          <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="email" value={data.email} onChange={(e) => update('email', e.target.value)}
            className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/10 outline-none"
            placeholder="fahrer@beispiel.de" data-testid="reg-email" />
        </div>
        <p className="text-xs text-slate-400 mt-1">Eine Aktivierungs-E-Mail wird an diese Adresse gesendet.</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">Passwort *</label>
        <div className="relative">
          <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type={showPassword ? 'text' : 'password'} value={data.password} onChange={(e) => update('password', e.target.value)}
            className="w-full pl-10 pr-12 py-3 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/10 outline-none"
            placeholder="Min. 6 Zeichen" data-testid="reg-password" />
          <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">Telefonnummer *</label>
        <div className="relative">
          <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="tel" value={data.phone} onChange={(e) => update('phone', e.target.value)}
            className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/10 outline-none"
            placeholder="+49 170 1234567" data-testid="reg-phone" />
        </div>
      </div>

      {error && <div className="bg-red-50 text-red-600 p-3 rounded-xl text-sm" data-testid="reg-error">{error}</div>}

      <label className="flex items-start gap-3 cursor-pointer" data-testid="reg-agb-label">
        <input type="checkbox" checked={agbAccepted} onChange={(e) => setAgbAccepted(e.target.checked)}
          className="w-5 h-5 mt-0.5 rounded border-slate-300 text-orange-500 focus:ring-orange-500 shrink-0" data-testid="reg-agb-checkbox" />
        <span className="text-sm text-slate-600 leading-relaxed">
          I accept the <a href="/fahrer-agb" target="_blank" rel="noopener noreferrer" className="text-orange-500 underline font-semibold">Driver Terms and Conditions</a> and the <a href="/agb" target="_blank" rel="noopener noreferrer" className="text-orange-500 underline font-semibold">General Terms and Conditions</a> of 366Transfer.
        </span>
      </label>

      <button onClick={onSubmit} disabled={loading || !canSubmit}
        className="w-full bg-orange-500 text-white py-3 rounded-xl font-semibold hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        data-testid="reg-submit-btn">
        {loading ? <><Loader2 size={18} className="animate-spin" /> Wird erstellt...</> : 'Konto erstellen'}
      </button>

      <p className="text-center text-sm text-slate-500">
        Bereits ein Konto? <a href="/" className="text-orange-500 font-semibold hover:text-orange-600">Anmelden</a>
      </p>
    </div>
  );
}

// ─── Step 2: Email Verification ───
function Step2Verify({ code, setCode, email, loading, error, resending, onVerify, onResend }) {
  return (
    <div className="p-6 space-y-5" data-testid="step-verify-email">
      <div className="text-center">
        <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Mail size={28} className="text-orange-500" />
        </div>
        <h2 className="text-lg font-bold text-slate-800 mb-1">E-Mail verifizieren</h2>
        <p className="text-sm text-slate-500">
          Wir haben einen 6-stelligen Code an <span className="font-semibold text-slate-700">{email}</span> gesendet.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5 text-center">Bestätigungscode</label>
        <input type="text" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          className="w-full px-4 py-4 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/10 outline-none text-center text-2xl tracking-[0.5em] font-mono font-bold"
          placeholder="000000" maxLength={6} data-testid="verify-code-input" autoFocus />
      </div>

      {error && <div className="bg-red-50 text-red-600 p-3 rounded-xl text-sm text-center" data-testid="verify-error">{error}</div>}

      <button onClick={onVerify} disabled={loading || code.length !== 6}
        className="w-full bg-orange-500 text-white py-3 rounded-xl font-semibold hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        data-testid="verify-submit-btn">
        {loading ? <><Loader2 size={18} className="animate-spin" /> Wird geprüft...</> : 'Code bestätigen'}
      </button>

      <p className="text-center text-sm text-slate-500">
        Keinen Code erhalten?{' '}
        <button onClick={onResend} disabled={resending} className="text-orange-500 font-semibold hover:text-orange-600 disabled:opacity-50" data-testid="resend-code-btn">
          {resending ? 'Gesendet!' : 'Erneut senden'}
        </button>
      </p>
    </div>
  );
}

// ─── Step 3: Driver Info ───
function Step3DriverInfo({ formData, cityOptions, onInputChange, onVehicleTypeChange, onLanguageToggle, loading, error, onSubmit }) {
  const [subStep, setSubStep] = useState(1); // 1 = personal info, 2 = vehicle
  const canProceed1 = formData.country && formData.region && formData.languages.length > 0;
  const canSubmit = formData.vehicle.make && formData.vehicle.model && formData.vehicle.plateNumber && formData.vehicle.color;

  return (
    <div className="p-6 space-y-6" data-testid="step-driver-info">
      {/* Sub-step tabs */}
      <div className="flex gap-2 border-b border-slate-100 pb-3">
        <button onClick={() => setSubStep(1)} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${subStep === 1 ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-600'}`}>
          <User size={14} className="inline mr-1.5" /> Basis-Info
        </button>
        <button onClick={() => { if (canProceed1) setSubStep(2); }} disabled={!canProceed1}
          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-40 ${subStep === 2 ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-600'}`}>
          <Car size={14} className="inline mr-1.5" /> Fahrzeug
        </button>
      </div>

      {subStep === 1 && (
        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              <Globe size={14} className="inline mr-1.5 text-orange-500" /> Land *
            </label>
            <SearchableSelect options={countryOptions} value={formData.country} onChange={(val) => onInputChange('country', val)} placeholder="Land suchen..." testId="country-select" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              <MapPin size={14} className="inline mr-1.5 text-orange-500" /> Region / Stadt *
            </label>
            {formData.country && cityOptions.length > 0 ? (
              <SearchableSelect options={cityOptions} value={formData.region} onChange={(val) => onInputChange('region', val)} placeholder="Stadt suchen..." testId="region-select" />
            ) : formData.country && cityOptions.length === 0 ? (
              <input type="text" value={formData.region} onChange={(e) => onInputChange('region', e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/10 outline-none"
                placeholder="Stadt / Region eingeben" data-testid="region-manual-input" />
            ) : (
              <SearchableSelect options={[]} value="" onChange={() => {}} placeholder="Bitte zuerst ein Land wählen" disabled testId="region-select" />
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Gesprochene Sprachen *</label>
            <div className="flex flex-wrap gap-2">
              {LANGUAGES.map((lang) => (
                <button key={lang.id} type="button" onClick={() => onLanguageToggle(lang.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${formData.languages.includes(lang.id) ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                  data-testid={`lang-${lang.id}`}>
                  {lang.name}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <input type="checkbox" id="isCompany" checked={formData.isCompany} onChange={(e) => onInputChange('isCompany', e.target.checked)} className="w-5 h-5 rounded border-slate-300 text-orange-500 focus:ring-orange-500" />
            <label htmlFor="isCompany" className="text-sm text-slate-700">Ich bin ein Transportunternehmen</label>
          </div>
          {formData.isCompany && (
            <input type="text" value={formData.companyName} onChange={(e) => onInputChange('companyName', e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/10 outline-none"
              placeholder="Name Ihres Unternehmens" />
          )}
          <button onClick={() => setSubStep(2)} disabled={!canProceed1}
            className="w-full bg-orange-500 text-white py-3 rounded-xl font-semibold hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="next-to-vehicle">
            Weiter zu Fahrzeug
          </button>
        </div>
      )}

      {subStep === 2 && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Marke *</label>
              <input type="text" value={formData.vehicle.make} onChange={(e) => onInputChange('vehicle.make', e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/10 outline-none"
                placeholder="z.B. Toyota" data-testid="vehicle-make" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Modell *</label>
              <input type="text" value={formData.vehicle.model} onChange={(e) => onInputChange('vehicle.model', e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/10 outline-none"
                placeholder="z.B. Camry" data-testid="vehicle-model" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Baujahr *</label>
              <input type="number" value={formData.vehicle.year} onChange={(e) => onInputChange('vehicle.year', e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/10 outline-none"
                min="2000" max={new Date().getFullYear() + 1} data-testid="vehicle-year" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Farbe *</label>
              <input type="text" value={formData.vehicle.color} onChange={(e) => onInputChange('vehicle.color', e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/10 outline-none"
                placeholder="z.B. Weiß" data-testid="vehicle-color" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Kennzeichen *</label>
            <input type="text" value={formData.vehicle.plateNumber} onChange={(e) => onInputChange('vehicle.plateNumber', e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/10 outline-none"
              placeholder="z.B. HRG 1234" data-testid="vehicle-plate" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Fahrzeugtyp *</label>
            <div className="grid grid-cols-3 gap-2">
              {VEHICLE_TYPES.map((type) => (
                <button key={type.id} type="button" onClick={() => onVehicleTypeChange(type.id)}
                  className={`p-3 rounded-xl text-sm font-medium transition-colors ${formData.vehicle.vehicleType === type.id ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                  data-testid={`type-${type.id}`}>
                  {type.name}
                  <div className="text-xs opacity-75">{type.passengers} Pers.</div>
                </button>
              ))}
            </div>
          </div>

          {error && <div className="bg-red-50 text-red-600 p-3 rounded-xl text-sm">{error}</div>}

          <div className="flex gap-3">
            <button onClick={() => setSubStep(1)} className="flex-1 bg-slate-100 text-slate-700 py-3 rounded-xl font-semibold hover:bg-slate-200 transition-colors">
              Zurück
            </button>
            <button onClick={onSubmit} disabled={loading || !canSubmit}
              className="flex-1 bg-emerald-500 text-white py-3 rounded-xl font-semibold hover:bg-emerald-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="submit-driver">
              {loading ? <><Loader2 size={18} className="animate-spin" /> Wird registriert...</> : 'Weiter'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Step 4: Document Upload ───
function Step4Documents({ vehicleId, docStatus, uploadingDoc, docUploaded, isPersonalDocUploaded, onPersonalUpload, onVehicleUpload, triggerFileInput, onFinish, onSkip, vehicleName }) {
  const PERSONAL_DOCS = [
    { key: 'selfie_with_license', label: 'Selfie mit Führerschein', required: true },
    { key: 'transport_license', label: 'Transportlizenz (optional)', required: false },
  ];
  const VEHICLE_DOCS = [
    { key: 'vehicle_registration', label: 'Fahrzeugzulassung', required: true },
    { key: 'vehicle_photo', label: 'Fahrzeugfoto', required: true },
    { key: 'insurance', label: 'Versicherungsnachweis', required: true },
  ];

  const vehiclesInfo = docStatus?.vehicles || [];
  const currentVehicle = vehiclesInfo.find(v => v.vehicle_id === vehicleId) || vehiclesInfo[0];
  const missingCount = currentVehicle?.missing?.length || 0;

  return (
    <div className="p-6 space-y-6" data-testid="step-documents">
      <div className="text-center mb-2">
        <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <FileText size={24} className="text-orange-500" />
        </div>
        <h2 className="text-lg font-bold text-slate-800">Dokumente hochladen</h2>
        <p className="text-sm text-slate-500 mt-1">Bitte laden Sie alle erforderlichen Dokumente hoch, um Angebote abgeben zu können.</p>
      </div>

      {/* Personal Documents */}
      <div>
        <h3 className="text-sm font-bold text-slate-700 mb-3">Persönliche Dokumente</h3>
        <div className="space-y-3">
          {PERSONAL_DOCS.map((doc) => (
            <DocItem
              key={doc.key}
              docKey={doc.key}
              label={doc.label}
              required={doc.required}
              isUploaded={isPersonalDocUploaded(doc.key)}
              isUploading={uploadingDoc === doc.key}
              onCamera={() => triggerFileInput((file) => onPersonalUpload(doc.key, file), true)}
              onUpload={() => triggerFileInput((file) => onPersonalUpload(doc.key, file), false)}
            />
          ))}
        </div>
      </div>

      {/* Vehicle Documents */}
      {vehiclesInfo.map((vInfo) => (
        <div key={vInfo.vehicle_id}>
          <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
            <Car size={14} className="text-slate-400" />
            {vInfo.vehicle_name || vehicleName || 'Fahrzeug'}
            {vInfo.complete ? (
              <span className="text-xs font-bold bg-emerald-100 text-emerald-700 px-2.5 py-0.5 rounded-full">Vollständig</span>
            ) : (
              <span className="text-xs font-bold bg-amber-100 text-amber-700 px-2.5 py-0.5 rounded-full">{vInfo.missing?.length} fehlt</span>
            )}
          </h3>
          <div className="space-y-3">
            {VEHICLE_DOCS.map((doc) => {
              const uploadKey = `${vInfo.vehicle_id}_${doc.key}`;
              const isMissing = vInfo.missing?.includes(doc.key);
              const uploaded = docUploaded[uploadKey] || !isMissing;
              return (
                <DocItem
                  key={uploadKey}
                  docKey={uploadKey}
                  label={doc.label}
                  required={doc.required}
                  isUploaded={uploaded}
                  isUploading={uploadingDoc === uploadKey}
                  onCamera={() => triggerFileInput((file) => onVehicleUpload(vInfo.vehicle_id, doc.key, file), true)}
                  onUpload={() => triggerFileInput((file) => onVehicleUpload(vInfo.vehicle_id, doc.key, file), false)}
                />
              );
            })}
          </div>
        </div>
      ))}

      {/* If no vehicle info available yet, show docs for the vehicleId directly */}
      {vehiclesInfo.length === 0 && vehicleId && (
        <div>
          <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
            <Car size={14} className="text-slate-400" />
            {vehicleName || 'Fahrzeug'}
            <span className="text-xs font-bold bg-amber-100 text-amber-700 px-2.5 py-0.5 rounded-full">3 fehlt</span>
          </h3>
          <div className="space-y-3">
            {VEHICLE_DOCS.map((doc) => {
              const uploadKey = `${vehicleId}_${doc.key}`;
              return (
                <DocItem
                  key={uploadKey}
                  docKey={uploadKey}
                  label={doc.label}
                  required={doc.required}
                  isUploaded={docUploaded[uploadKey]}
                  isUploading={uploadingDoc === uploadKey}
                  onCamera={() => triggerFileInput((file) => onVehicleUpload(vehicleId, doc.key, file), true)}
                  onUpload={() => triggerFileInput((file) => onVehicleUpload(vehicleId, doc.key, file), false)}
                />
              );
            })}
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="space-y-3 pt-2">
        <button onClick={onFinish}
          className="w-full py-3.5 rounded-xl bg-emerald-500 text-white font-bold hover:bg-emerald-600 transition-colors flex items-center justify-center gap-2 text-base"
          data-testid="doc-finish-btn">
          <CheckCircle size={20} /> Fertig & Weiter
        </button>
        <button onClick={onSkip}
          className="w-full py-3 rounded-xl border-2 border-slate-200 text-slate-500 font-semibold hover:bg-slate-50 transition-colors text-sm"
          data-testid="doc-skip-btn">
          Später Dokumentation hochladen
        </button>
      </div>
    </div>
  );
}
